<script setup lang="ts">
// NPM
import { ref } from 'vue';
import { useMq } from 'vue3-mq';
import { useI18n } from 'vue-i18n';

// Components
import {
  BaseCard,
  BaseEmptyState,
  Modal,
  BaseButton,
  BaseBadge,
  FieldMultiFileUploader,
  BaseIcon,
  FieldTextarea,
  LinkedText,
} from '@magma-app/magma-lapilli';

// API
import { useEvents } from '@/api/events/use-events';
import { useConfirmParticipation } from '@/api/events/use-confirm-participation';
import { useActionFileUpload } from '@/composables/useUgcFileUpload';
import { useActionDeepLink } from '@/composables/useActionDeepLink';
import { isEventReadyForAmbassadorAction } from '@/composables/useHasPendingAction';
import type { UserDashboardGetAllEventsResponseType } from '@magma-app/magma-types/user-dashboard/event';

// Plugins
const mq = useMq();
const { locale } = useI18n();

type OngoingEvent = UserDashboardGetAllEventsResponseType['ongoing'][number];

const { data: events, isLoading, refetch: refetchEvents } = useEvents();
const {
  mutateAsync: confirmParticipation,
  isPending: isConfirmingParticipation,
} = useConfirmParticipation();

const MAX_ACTION_FILE_SIZE = 300 * 1000 * 1000;
const ACTION_FILE_ACCEPT = '.png,.jpg,.jpeg,.gif,.webp,.avif,.heic,.mp4,.mov,.webm,.mkv,.m4v,.pdf';

const getBaseCardStatus = (event: any) => {
  if (isEventReadyForAmbassadorAction(event)) {
    return 'eventActionNeeded';
  }

  if (getModalState(event) === 'participation.waiting') {
    return 'eventToValidate';
  }

  return null;
};

// Reactive variables
const modal = ref<number | null>(null);
const currentModalStep = ref<'info' | 'add' | 'end' | 'waiting'>('info');
const selectedUserEventId = ref(0);
const proofFiles = ref<File[]>([]);
const fileUpload = useActionFileUpload(proofFiles, 'event', selectedUserEventId);
const submissionError = ref(false);
const proof = ref({
  textProof: '',
});

const getEventImgUrl = (event: { pictureUrl?: string | null }) =>
  event.pictureUrl
    ? event.pictureUrl
    : mq.lgPlus
      ? '/img/event_lg.png'
      : '/img/event.png';

// Format date helper
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString(locale.value, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

// Determine modal state based on event status
const getModalState = (event: any) => {
  if (event.participationStatus) return 'participation.closed';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const eventDate = new Date(event.date);
  eventDate.setHours(0, 0, 0, 0);

  // Inscription not validated by admin yet
  if (event.registrationStatus === null) {
    return 'inscription.waiting';
  }

  // Inscription rejected by admin
  if (event.registrationStatus === 'rejected') {
    return 'inscription.rejected';
  }

  // Inscription confirmed but event date is in the future
  if (event.registrationStatus === 'validated' && eventDate > today) {
    return 'inscription.confirmed';
  }

  // Inscription confirmed and event date is today or past, but no participation proof yet
  if (
    event.registrationStatus === 'validated' &&
    eventDate <= today &&
    !event.participationConfirmedAt
  ) {
    return 'can-validate-participation';
  }

  // Participation proof submitted, waiting for admin validation
  if (event.participationConfirmedAt && event.participationStatus === null) {
    return 'participation.waiting';
  }

  // Default fallback
  return 'inscription.waiting';
};

const resetProof = () => {
  proof.value.textProof = '';
  proofFiles.value = [];
  submissionError.value = false;
  fileUpload.clearAll();
};

const resetModal = () => {
  resetProof();
  currentModalStep.value = 'info';
  selectedUserEventId.value = 0;
};

const openEventModal = (event: OngoingEvent) => {
  void refetchEvents();
  modal.value = event.eventId;
  selectedUserEventId.value = event.userEventId || 0;
  currentModalStep.value =
    getModalState(event) === 'participation.waiting' ? 'waiting' : 'info';
  refreshProofUrlsIfNeeded(event);
};

const closeEventModal = () => {
  modal.value = null;
  resetModal();
};

// Opens one event's modal directly from a ?eventId= deep link.
useActionDeepLink<OngoingEvent>({
  type: 'event',
  bucket: 'ongoing',
  onOpen: (event) => openEventModal(event),
});

const getProofFiles = (event: any) => {
  if (event.proofFiles?.length) {
    return event.proofFiles;
  }

  if (!event.fileProofUrl) {
    return [];
  }

  return [{
    id: -(event.userEventId || 0),
    url: event.fileProofUrl,
    originalFilename: 'proof-file',
    filename: 'proof-file',
    mimeType: 'application/octet-stream',
    size: 0,
  }];
};

const refreshProofUrlsIfNeeded = (event: any) => {
  if (
    getModalState(event) === 'participation.waiting'
    && getProofFiles(event).length
  ) {
    void refetchEvents();
  }
};

const submitParticipation = async (event: any) => {
  if (event.participationStatus) return;
  submissionError.value = false;

  try {
    await confirmParticipation({
      userEventId: event.userEventId || 0,
      textProof: proof.value.textProof.trim() || undefined,
      fileKeys: fileUpload.completedFiles.value.length
        ? fileUpload.completedFiles.value
        : undefined,
    });
    currentModalStep.value = 'end';
  } catch {
    await refetchEvents();
    submissionError.value = true;
  }
};
</script>

<template>
  <div
    v-if="!isLoading"
    class="block h-full flex-1 px-4 md:px-8"
    :class="{
      'content-start lg:flex lg:flex-wrap': events?.ongoing.length
    }"
  >
    <template v-if="events?.ongoing.length">
      <Modal
        v-for="event in events?.ongoing"
        :key="event.userEventId"
        :open="modal === event.eventId"
        content-class="!max-h-[calc(100dvh-2rem)] max-w-[90dvw] !overflow-y-auto overscroll-contain sm:max-w-[480px]"
        @open-focus="(e) => e.preventDefault()"
        @open-change="
          (open) => (open ? openEventModal(event) : closeEventModal())
        "
      >
        <template #trigger>
          <BaseCard
            class="mb-2 lg:mb-6 lg:mr-6"
            :title="event.name"
            :img-url="getEventImgUrl(event)"
            :points="event.points ?? undefined"
            :badge-status="{ points: event.points ?? 0, type: 'event' }"
            :display-points="!event.pictureUrl"
            :date="formatDate(event.date)"
            :status="getBaseCardStatus(event)"
          />
        </template>
        <template #content>
          <!-- Waiting for inscription validation -->
          <div
            v-if="getModalState(event) === 'inscription.waiting'"
            class="flex flex-col gap-4"
          >
            <img
              :src="getEventImgUrl(event)"
              :alt="event.name"
              class="h-[200px] w-full rounded-[5px] object-cover"
            />
            <div class="flex flex-col gap-2">
              <h3
                class="text-mgm-txt-lg font-semibold text-foreground-emphasis"
              >
                {{ event.name }}
              </h3>
              <div class="flex flex-row gap-4 items-center">
                <div class="flex flex-row gap-2 items-center">
                  <BaseIcon icon="calendar" color="#5F6C85" />
                  <p class="text-mgm-txt-sm text-foreground-default">
                    {{ formatDate(event.date) }}
                  </p>
                </div>
                <BaseBadge
                  size="sm"
                  :text="event.points ?? 0"
                  icon-type="icon"
                  icon-name="star"
                />
              </div>
            </div>
            <div
              class="
                rich-text-content max-h-[40dvh] min-w-0 break-words
                overflow-y-auto text-mgm-txt-sm text-foreground-default
                lg:max-h-none
              "
              v-html="event.description"
            ></div>
            <div
              class="text-mgm-txt-sm font-medium border border-border-default rounded-[40px] bg-background-subtle text-foreground-default px-4 py-[10px] flex items-center justify-center mt-2"
            >
              {{ $t('events.ongoing.inscription.waiting') }}
            </div>
          </div>

          <!-- Inscription rejected -->
          <div
            v-if="getModalState(event) === 'inscription.rejected'"
            class="flex flex-col gap-4"
          >
            <img
              :src="getEventImgUrl(event)"
              :alt="event.name"
              class="h-[200px] w-full rounded-[5px] object-cover"
            />
            <div class="flex flex-col gap-2">
              <h3
                class="text-mgm-txt-lg font-semibold text-foreground-emphasis"
              >
                {{ event.name }}
              </h3>
              <div class="flex flex-row gap-4 items-center">
                <div class="flex flex-row gap-2 items-center">
                  <BaseIcon icon="calendar" color="#5F6C85" />
                  <p class="text-mgm-txt-sm text-foreground-default">
                    {{ formatDate(event.date) }}
                  </p>
                </div>
                <BaseBadge
                  size="sm"
                  :text="event.points ?? 0"
                  icon-type="icon"
                  icon-name="star"
                />
              </div>
            </div>
            <div
              class="
                rich-text-content max-h-[40dvh] min-w-0 break-words
                overflow-y-auto text-mgm-txt-sm text-foreground-default
                lg:max-h-none
              "
              v-html="event.description"
            ></div>
            <div
              class="text-mgm-txt-sm font-medium border border-border-danger-default rounded-[40px] bg-background-danger-default text-foreground-danger-default px-4 py-[10px] flex items-center justify-center mt-2"
            >
              {{ $t('events.rejected.cta') }}
            </div>
          </div>

          <!-- Inscription confirmed -->
          <div
            v-if="getModalState(event) === 'inscription.confirmed'"
            class="flex flex-col gap-4"
          >
            <img
              :src="getEventImgUrl(event)"
              :alt="event.name"
              class="h-[200px] w-full rounded-[5px] object-cover"
            />
            <div class="flex flex-col gap-2">
              <h3
                class="text-mgm-txt-lg font-semibold text-foreground-emphasis"
              >
                {{ event.name }}
              </h3>
              <div class="flex flex-row gap-4 items-center">
                <div class="flex flex-row gap-2 items-center">
                  <BaseIcon icon="calendar" color="#5F6C85" />
                  <p class="text-mgm-txt-sm text-foreground-default">
                    {{ formatDate(event.date) }}
                  </p>
                </div>
                <BaseBadge
                  size="sm"
                  :text="event.points ?? 0"
                  icon-type="icon"
                  icon-name="star"
                />
              </div>
            </div>
            <div
              class="
                rich-text-content max-h-[40dvh] min-w-0 break-words
                overflow-y-auto text-mgm-txt-sm text-foreground-default
                lg:max-h-none
              "
              v-html="event.description"
            ></div>
            <div
              class="text-mgm-txt-sm font-medium border border-border-success-emphasis rounded-[40px] bg-background-success-default text-foreground-success-default px-4 py-[10px] flex items-center justify-center mt-2"
            >
              {{ $t('events.ongoing.inscription.confirmed') }}
            </div>
          </div>

          <!-- Can validate participation - Info step -->
          <div
            v-if="
              getModalState(event) === 'can-validate-participation' &&
              currentModalStep === 'info'
            "
            class="flex flex-col gap-4"
          >
            <img
              :src="getEventImgUrl(event)"
              :alt="event.name"
              class="h-[200px] w-full rounded-[5px] object-cover"
            />
            <div class="flex flex-col gap-2">
              <h3
                class="text-mgm-txt-lg font-semibold text-foreground-emphasis"
              >
                {{ event.name }}
              </h3>
              <div class="flex flex-row gap-4 items-center">
                <div class="flex flex-row gap-2 items-center">
                  <BaseIcon icon="calendar" color="#5F6C85" />
                  <p class="text-mgm-txt-sm text-foreground-default">
                    {{ formatDate(event.date) }}
                  </p>
                </div>
                <BaseBadge
                  size="sm"
                  :text="event.points ?? 0"
                  icon-type="icon"
                  icon-name="star"
                />
              </div>
            </div>
            <div
              class="
                rich-text-content max-h-[40dvh] min-w-0 break-words
                overflow-y-auto text-mgm-txt-sm text-foreground-default
                lg:max-h-none
              "
              v-html="event.description"
            ></div>
            <BaseButton
              size="lg"
              class="w-full mt-2"
              @click="currentModalStep = 'add'"
            >
              {{ $t('events.ongoing.validate-participation') }}
            </BaseButton>
          </div>

          <!-- Can validate participation - Add proof step -->
          <div
            v-if="
              getModalState(event) === 'can-validate-participation' &&
              currentModalStep === 'add'
            "
            class="flex flex-col gap-4"
          >
            <div
              class="w-12 h-12 bg-[#F1F5F9] rounded-full border-8 border-[#F8FAFC] flex items-center justify-center"
            >
              <BaseIcon icon="upload" color="#5F6C85" />
            </div>
            <h3 class="text-mgm-txt-lg font-semibold text-foreground-emphasis">
              {{ $t('events.ongoing.add.title') }}
            </h3>
            <p class="text-mgm-txt-sm text-foreground-default whitespace-pre-line">
              <LinkedText :text="event.descriptionProof" />
            </p>

            <FieldTextarea
              v-if="event.needTextProof"
              v-model="proof.textProof"
              :label="$t('events.ongoing.add.comment')"
              class="mt-2"
              :required="true"
            />

            <FieldMultiFileUploader
              v-if="event.needFileProof"
              :id="`event-file-${event.userEventId}`"
              v-model="proofFiles"
              :label="$t('events.ongoing.add.file')"
              class="mt-2"
              :upload-states="fileUpload.uploadStates.value"
              :max-file-size="MAX_ACTION_FILE_SIZE"
              :accept="ACTION_FILE_ACCEPT"
              :required="true"
              @file-remove="fileUpload.onFileRemoved"
              @file-rename="fileUpload.renameFile"
              @file-retry="fileUpload.retryFile"
            />

            <p
              v-if="submissionError"
              class="text-mgm-txt-xs text-foreground-danger-default"
            >
              {{ $t('generic.error') }}
            </p>

            <div class="flex gap-2 mt-2">
              <BaseButton
                size="lg"
                class="flex-1"
                state="scdr"
                :disabled="isConfirmingParticipation || fileUpload.isUploading.value"
                @click="currentModalStep = 'info'"
              >
                {{ $t('events.ongoing.add.back') }}
              </BaseButton>
              <BaseButton
                size="lg"
                class="flex-1"
                :loading="isConfirmingParticipation"
                :disabled="
                  (event.needTextProof && !proof.textProof.trim()) ||
                  (event.needFileProof && !fileUpload.allUploaded.value) ||
                  fileUpload.isUploading.value ||
                  fileUpload.hasErrors.value ||
                  isConfirmingParticipation
                "
                @click="submitParticipation(event)"
              >
                {{ $t('generic.validate') }}
              </BaseButton>
            </div>
          </div>

          <!-- Participation confirmation -->
          <div
            v-if="
              (getModalState(event) === 'can-validate-participation' ||
                getModalState(event) === 'participation.waiting') &&
              currentModalStep === 'end'
            "
            class="flex flex-col gap-4"
          >
            <div
              class="w-12 h-12 bg-[#D1FADF] rounded-full border-8 border-[#ECFDF3] flex items-center justify-center"
            >
              <BaseIcon icon="check-circle" color="#03874C" />
            </div>
            <div class="flex flex-col gap-2">
              <h3
                class="text-mgm-txt-lg font-semibold text-foreground-emphasis"
              >
                {{ $t('events.ongoing.end.title') }}
              </h3>
              <p class="text-mgm-txt-sm text-foreground-default">
                {{
                  $t('events.ongoing.end.content', {
                    email: event.contactAdminEmail
                  })
                }}
              </p>
            </div>
            <BaseButton
              size="lg"
              class="w-full mt-2"
              @click="
                () => {
                  modal = null;
                  resetModal();
                }
              "
            >
              {{ $t('generic.close') }}
            </BaseButton>
          </div>

          <!-- Waiting for participation validation -->
          <div
            v-if="
              getModalState(event) === 'participation.waiting' &&
              currentModalStep === 'waiting'
            "
            class="flex flex-col gap-4"
          >
            <img
              :src="getEventImgUrl(event)"
              :alt="event.name"
              class="h-[200px] w-full rounded-[5px] object-cover"
            />
            <div class="flex flex-col gap-2">
              <h3
                class="text-mgm-txt-lg font-semibold text-foreground-emphasis"
              >
                {{ event.name }}
              </h3>
              <div class="flex flex-row gap-4 items-center">
                <div class="flex flex-row gap-2 items-center">
                  <BaseIcon icon="calendar" color="#5F6C85" />
                  <p class="text-mgm-txt-sm text-foreground-default">
                    {{ formatDate(event.date) }}
                  </p>
                </div>
                <BaseBadge
                  size="sm"
                  :text="event.points ?? 0"
                  icon-type="icon"
                  icon-name="star"
                />
              </div>
            </div>
            <div
              class="text-mgm-txt-sm text-foreground-default max-h-[40dvh] lg:max-h-auto overflow-y-auto whitespace-pre-line"
            >
              <LinkedText :text="event.descriptionProof" />
            </div>

            <!-- Display submitted proofs -->
            <div
              v-if="event.textProof || getProofFiles(event).length"
              class="flex flex-col gap-3 mt-4"
            >
              <FieldTextarea
                v-if="event.textProof"
                :model-value="event.textProof"
                :label="$t('events.ongoing.participation.comment')"
                :disabled="true"
              />

              <FieldMultiFileUploader
                v-if="getProofFiles(event).length"
                :id="`event-file-display-${event.userEventId}`"
                :label="$t('events.ongoing.participation.file')"
                :readonly-files="getProofFiles(event)"
                view-only
              />
            </div>
            <div
              class="text-mgm-txt-sm font-medium border border-border-warning-default rounded-[40px] bg-background-warning-default text-foreground-warning-default px-4 py-[10px] flex items-center justify-center mt-2"
            >
              {{ $t('events.ongoing.participation.waiting') }}
            </div>
          </div>
        </template>
      </Modal>
    </template>

    <BaseEmptyState
      v-else
      class="flex h-full flex-col items-center justify-center"
      :text="$t('eventsView.empty.all')"
      type="empty-list"
    />
  </div>
</template>
